import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import api, { workspaceAPI, spaceAPI, listAPI, customFieldAPI, webhookAPI, auditLogAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import CreateSpaceChannelModal from '../components/CreateSpaceChannelModal';
import SearchableSelect from '../components/SearchableSelect';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogOverlay,
} from '../components/ui/dialog';
import { cn, formatDate, formatDateTime, generateColor, customFieldTypes } from '../lib/utils';
import {
    Settings,
    User,
    Building,
    Layers,
    Columns,
    Plus,
    Trash2,
    Edit2,
    Loader2,
    Webhook,
    History,
    Type,
    Hash,
    Calendar,
    ChevronDown,
    List,
    CheckSquare,
    Link,
    DollarSign,
    Palette,
    ListTodo,
    GripVertical,
    Video,
    ClipboardList,
    Camera,
    ArchiveRestore,
    ArrowUpFromDotIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { set } from 'date-fns';
import {
  restrictToWindowEdges,
  restrictToParentElement,
  restrictToVerticalAxis,
  restrictToHorizontalAxis,
  snapCenterToCursor,
} from "@dnd-kit/modifiers";
import { useDroppable } from "@dnd-kit/core";

function GroupContainer({ group, children, enableDrop = true }) {
  const { setNodeRef } = useDroppable({ id: group });

  return (
    <div ref={setNodeRef} className="min-h-16 border border-dashed rounded-lg p-2 flex flex-col select-none">
      {children}
      {enableDrop && children.length === 0 && (
        <div className="text-xs text-gray-400 text-center my-auto">Drop here</div>
      )}
      {!enableDrop && children.length === 0 && (
        <div className="text-xs text-gray-400 text-center my-auto">No items</div>
      )}
    </div>
  );
}

// Sortable Status Item Component
const SortableStatusItem = ({ status, index, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: status.name });

    const style = {
        transform: transform
            ? CSS.Transform.toString({
                ...transform,   // ambil x dan y
                scaleX: 1,
                scaleY: 1,
            })
            : undefined,
        transition,
        opacity: isDragging ? 0.7 : 1,
        zIndex: isDragging ? 999 : "auto",
        cursor: isDragging ? "grabbing" : "pointer",
    };


    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                "flex items-center gap-3 p-3 bg-brand-background rounded-lg group max-h-16 select-none",
                isDragging && "shadow-lg ring-2 ring-brand-primary max-h-16"
            )}
        >
            <button
                className="cursor-grab active:cursor-grabbing text-brand-text-muted hover:text-brand-text-primary max-h-16"
                {...attributes}
                {...listeners}
            >
                <GripVertical className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <div
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: status.color }}
            />
            <span className="flex-1 text-sm font-medium">{status.name}</span>
            <span className="text-xs text-brand-text-muted">Order: {index}</span>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => onEdit(status)}
                >
                    <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Button>
                <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-brand-error hover:bg-brand-error/10"
                    onClick={() => onDelete(status.name)}
                >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Button>
            </div>
        </div>
    );
};

export default function SettingsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || 'profile';
    const workspaceId = searchParams.get('wsid') || '';
    const { currentWorkspace, fetchWorkspaces, selectWorkspace, workspaces, canManageSettings } = useWorkspace();
    const [initializing, setInitializing] = useState(true);

    // Auto-select first workspace if none selected
    useEffect(() => {
        const initWorkspace = async () => {
            if (!currentWorkspace) {
                const ws = await fetchWorkspaces();
                if (ws.length > 0) {
                    const workspace =  ws.find((w) => w.id === workspaceId);
                    await selectWorkspace(workspace || ws[0]);
                }
            }
            setInitializing(false);
        };
        initWorkspace();
    }, []);

    const setActiveTab = (tab) => {
        setSearchParams({ tab });
    };

    if (initializing) {
        return (
            <Layout title="Settings">
                <div className="flex items-center justify-center h-64">
                    <div className="spinner" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout title={ currentWorkspace ? `Settings of ${currentWorkspace.name} Workspace` : 'Settings' } subtitle="Manage your workspace and preferences">
            <div className="max-w-4xl" data-testid="settings-page">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="bg-brand-accent-soft/50 p-1 flex-wrap">
                        <TabsTrigger value="profile" className="data-[state=active]:bg-white">
                            <User className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Profile
                        </TabsTrigger>
                        {canManageSettings && <TabsTrigger value="workspace" className="data-[state=active]:bg-white">
                            <Building className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Workspace
                        </TabsTrigger> }
                        <TabsTrigger value="spaces" className="data-[state=active]:bg-white">
                            <Layers className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Spaces
                        </TabsTrigger>
                        <TabsTrigger value="lists" className="data-[state=active]:bg-white">
                            <ListTodo className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Lists/Sprints
                        </TabsTrigger>
                        <TabsTrigger value="custom-fields" className="data-[state=active]:bg-white">
                            <Columns className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Custom Fields
                        </TabsTrigger>
                        {canManageSettings && <TabsTrigger value="webhooks" className="data-[state=active]:bg-white">
                            <Webhook className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Webhooks
                        </TabsTrigger>}
                        {canManageSettings && <TabsTrigger value="audit" className="data-[state=active]:bg-white">
                            <History className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Audit Log
                        </TabsTrigger>}
                    </TabsList>

                    <TabsContent value="profile">
                        <ProfileSettings />
                    </TabsContent>

                    <TabsContent value="workspace">
                        <WorkspaceSettings />
                    </TabsContent>

                    <TabsContent value="spaces">
                        <SpacesSettings />
                    </TabsContent>

                    <TabsContent value="lists">
                        <ListsSettings />
                    </TabsContent>

                    <TabsContent value="custom-fields">
                        <CustomFieldsSettings />
                    </TabsContent>

                    <TabsContent value="webhooks">
                        <WebhooksSettings />
                    </TabsContent>

                    <TabsContent value="audit">
                        <AuditLogSettings />
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
}

// Profile Settings
const ProfileSettings = () => {
    const { user, updateUser } = useAuth();
    const [name, setName] = useState(user?.name || '');
    const [phone, setPhone] = useState(user?.phone || '');
    const [avatar, setAvatar] = useState(user?.avatar || '');
    const [avatarPreview, setAvatarPreview] = useState(user?.avatar || '');
    const [saving, setSaving] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const fileInputRef = React.useRef(null);

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }
        
        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Image size must be less than 2MB');
            return;
        }

        // Preview
        const reader = new FileReader();
        reader.onload = (e) => setAvatarPreview(e.target.result);
        reader.readAsDataURL(file);

        // Upload
        setUploadingAvatar(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setAvatar(response.data.url);
            toast.success('Avatar uploaded');
        } catch (error) {
            toast.error('Failed to upload avatar');
            setAvatarPreview(avatar); // Revert preview
        } finally {
            setUploadingAvatar(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put('/auth/profile', { name, phone, avatar });
            if (updateUser) {
                updateUser({ ...user, name, phone, avatar });
            }
            toast.success('Profile updated');
        } catch (error) {
            toast.error('Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
        <Card className="border-brand-border shadow-card">
            <CardHeader>
                <CardTitle>Profile Settings</CardTitle>
                <CardDescription>Manage your personal information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Avatar Section */}
                <div>
                    <Label className="mb-3 block">Profile Photo</Label>
                    <div className="flex items-center gap-4">
                        <div className="relative">
                            {avatarPreview ? (
                                <img 
                                    src={avatarPreview} 
                                    alt="Avatar" 
                                    className="w-20 h-20 rounded-full object-cover border-2 border-brand-border"
                                />
                            ) : (
                                <div className="w-20 h-20 rounded-full bg-brand-primary text-white flex items-center justify-center text-xl font-semibold">
                                    {getInitials(name)}
                                </div>
                            )}
                            {uploadingAvatar && (
                                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                                    <Loader2 className="w-6 h-6 animate-spin text-white" />
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleAvatarChange}
                                accept="image/*"
                                className="hidden"
                            />
                            <Button 
                                type="button"
                                variant="outline" 
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingAvatar}
                            >
                                <Camera className="w-4 h-4 mr-2" />
                                Change Photo
                            </Button>
                            <p className="text-xs text-brand-text-muted">JPG, PNG. Max 2MB</p>
                        </div>
                    </div>
                </div>

                {/* Name */}
                <div>
                    <Label>Full Name</Label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-1 max-w-md"
                        data-testid="profile-name-input"
                    />
                </div>

                {/* Email */}
                <div>
                    <Label>Email</Label>
                    <Input
                        value={user?.email || ''}
                        disabled
                        className="mt-1 max-w-md bg-brand-background"
                    />
                    <p className="text-xs text-brand-text-muted mt-1">Email cannot be changed</p>
                </div>

                {/* Phone */}
                <div>
                    <Label>Phone Number</Label>
                    <Input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+62 812 3456 7890"
                        className="mt-1 max-w-md"
                        data-testid="profile-phone-input"
                    />
                    <p className="text-xs text-brand-text-muted mt-1">Used for WhatsApp notifications</p>
                </div>

                <Button 
                    onClick={handleSave} 
                    className="bg-brand-primary hover:bg-brand-primary-dark"
                    disabled={saving}
                >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Changes
                </Button>
            </CardContent>
        </Card>
    );
};

// Workspace Settings
const WorkspaceSettings = () => {
    const { currentWorkspace, fetchWorkspaces, selectWorkspace } = useWorkspace();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [color, setColor] = useState('#2F6F63');
    const [saving, setSaving] = useState(false);
    
    // Meeting settings
    const [meetingProvider, setMeetingProvider] = useState('jitsi');
    const [jitsiUrl, setJitsiUrl] = useState('https://meet.jit.si');
    const [savingMeeting, setSavingMeeting] = useState(false);

    // Daily Standup settings
    const [standupEnabled, setStandupEnabled] = useState(false);
    const [savingStandup, setSavingStandup] = useState(false);

    useEffect(() => {
        if (currentWorkspace) {
            setName(currentWorkspace.name);
            setDescription(currentWorkspace.description || '');
            setColor(currentWorkspace.color || '#2F6F63');
            loadMeetingSettings();
        }
    }, [currentWorkspace]);

    const loadMeetingSettings = async () => {
        if (!currentWorkspace) return;
        try {
            const res = await workspaceAPI.getSettings(currentWorkspace.id);
            setMeetingProvider(res.data.meeting_provider || 'jitsi');
            setJitsiUrl(res.data.jitsi_url || 'https://meet.jit.si');
            setStandupEnabled(res.data.daily_standup_enabled === true);
        } catch (error) {
            console.error('Failed to load meeting settings:', error);
        }
    };

    const handleSave = async () => {
        if (!currentWorkspace) return;
        setSaving(true);
        try {
            await workspaceAPI.update(currentWorkspace.id, { name, description, color });
            toast.success('Workspace updated');
            await fetchWorkspaces();
        } catch (error) {
            toast.error('Failed to update workspace');
        } finally {
            setSaving(false);
        }
    };
    
    const handleSaveMeetingSettings = async () => {
        if (!currentWorkspace) return;
        setSavingMeeting(true);
        try {
            await workspaceAPI.updateSettings(currentWorkspace.id, {
                meeting_provider: meetingProvider,
                jitsi_url: jitsiUrl
            });
            toast.success('Meeting settings updated');
        } catch (error) {
            toast.error('Failed to update meeting settings');
        } finally {
            setSavingMeeting(false);
        }
    };

    const handleSaveStandupSettings = async () => {
        if (!currentWorkspace) return;
        setSavingStandup(true);
        try {
            await workspaceAPI.updateSettings(currentWorkspace.id, {
                daily_standup_enabled: standupEnabled
            });
            // Refresh workspace context — sidebar & popup membaca daily_standup_enabled
            // dari currentWorkspace, jadi tanpa ini perlu reload manual.
            await fetchWorkspaces();
            toast.success(standupEnabled ? 'Daily Standup diaktifkan' : 'Daily Standup dinonaktifkan');
        } catch (error) {
            toast.error('Gagal menyimpan pengaturan Daily Standup');
        } finally {
            setSavingStandup(false);
        }
    };

    const colorOptions = ['#2F6F63', '#1F4D45', '#7ED957', '#F5C26B', '#F26B6B', '#6EC1E4'];

    return (
        <div className="space-y-6">
            <Card className="border-brand-border shadow-card">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>Workspace Settings</CardTitle>
                        <CardDescription>Configure your workspace details</CardDescription>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {currentWorkspace ? (
                        <>
                            <div>
                                <Label>Workspace Name</Label>
                                <Input
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="mt-1 max-w-md"
                                    data-testid="workspace-name-input"
                                />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="mt-1 max-w-md"
                                    placeholder="Optional description"
                                />
                            </div>
                            <div>
                                <Label>Color</Label>
                                <div className="flex gap-2 mt-2">
                                    {colorOptions.map(c => (
                                        <button
                                            key={c}
                                            onClick={() => setColor(c)}
                                            className={cn(
                                                'w-8 h-8 rounded-full transition-all',
                                                color === c && 'ring-2 ring-offset-2 ring-brand-primary'
                                            )}
                                            style={{ backgroundColor: c }}
                                        />
                                    ))}
                                </div>
                            </div>
                            <Button 
                                onClick={handleSave} 
                                className="bg-brand-primary hover:bg-brand-primary-dark"
                                disabled={saving}
                            >
                                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Save Changes
                            </Button>
                        </>
                    ) : (
                        <p className="text-brand-text-muted">Create a workspace to get started</p>
                    )}
                </CardContent>
            </Card>
            
            {/* Meeting Settings */}
            {currentWorkspace && (
                <Card className="border-brand-border shadow-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Video className="w-5 h-5 text-purple-500" />
                            Meeting Settings
                        </CardTitle>
                        <CardDescription>Configure video meeting provider for calendar meetings</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label>Meeting Provider</Label>
                            <div className="flex gap-4 mt-2">
                                <label className={cn(
                                    "flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all",
                                    meetingProvider === 'jitsi' 
                                        ? "border-purple-500 bg-purple-50" 
                                        : "border-gray-200 hover:border-gray-300"
                                )}>
                                    <input 
                                        type="radio" 
                                        name="provider" 
                                        value="jitsi"
                                        checked={meetingProvider === 'jitsi'}
                                        onChange={(e) => setMeetingProvider(e.target.value)}
                                        className="hidden"
                                    />
                                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold">
                                        J
                                    </div>
                                    <div>
                                        <p className="font-medium">Jitsi Meet</p>
                                        <p className="text-xs text-gray-500">Free, open-source video conferencing</p>
                                    </div>
                                </label>
                                
                                <label className={cn(
                                    "flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all",
                                    meetingProvider === 'google_meet' 
                                        ? "border-purple-500 bg-purple-50" 
                                        : "border-gray-200 hover:border-gray-300"
                                )}>
                                    <input 
                                        type="radio" 
                                        name="provider" 
                                        value="google_meet"
                                        checked={meetingProvider === 'google_meet'}
                                        onChange={(e) => setMeetingProvider(e.target.value)}
                                        className="hidden"
                                    />
                                    <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold">
                                        G
                                    </div>
                                    <div>
                                        <p className="font-medium">Google Meet</p>
                                        <p className="text-xs text-gray-500">Google's video conferencing</p>
                                    </div>
                                </label>
                            </div>
                        </div>
                        
                        {meetingProvider === 'jitsi' && (
                            <div>
                                <Label>Custom Jitsi URL</Label>
                                <Input
                                    value={jitsiUrl}
                                    onChange={(e) => setJitsiUrl(e.target.value)}
                                    className="mt-1 max-w-md"
                                    placeholder="https://meet.jit.si"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Default: https://meet.jit.si. Anda bisa menggunakan self-hosted Jitsi server.
                                </p>
                            </div>
                        )}
                        
                        <Button 
                            onClick={handleSaveMeetingSettings}
                            className="bg-purple-600 hover:bg-purple-700"
                            disabled={savingMeeting}
                        >
                            {savingMeeting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Save Meeting Settings
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Daily Standup Settings */}
            {currentWorkspace && (
                <Card className="border-brand-border shadow-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <ClipboardList className="w-5 h-5 text-brand-accent" />
                            Daily Standup
                        </CardTitle>
                        <CardDescription>
                            Aktifkan popup standup harian dan menu Project Manager Area untuk workspace ini
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center justify-between max-w-md">
                            <div className="pr-4">
                                <Label>Aktifkan Daily Standup</Label>
                                <p className="text-xs text-gray-500 mt-1">
                                    Saat nonaktif, popup standup tidak muncul dan menu Daily Standup disembunyikan.
                                </p>
                            </div>
                            <Switch
                                checked={standupEnabled}
                                onCheckedChange={setStandupEnabled}
                                data-testid="daily-standup-toggle"
                            />
                        </div>

                        <Button
                            onClick={handleSaveStandupSettings}
                            className="bg-brand-primary hover:bg-brand-primary-dark"
                            disabled={savingStandup}
                            data-testid="daily-standup-save"
                        >
                            {savingStandup && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Simpan
                        </Button>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};


const SpacesSettings = () => {
    const { currentWorkspace, spaces, setSpaces, selectSpace, currentUserRole } = useWorkspace();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSpace, setEditingSpace] = useState(null);
    const [creatingChannelFor, setCreatingChannelFor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [spaceChannels, setSpaceChannels] = useState({});
    const [archivedSpaces, setArchivedSpaces] = useState([]);

    const loadArchivedSpaces = async () => {
        if (!currentWorkspace) return;
        try {
            const res = await spaceAPI.getByWorkspace(currentWorkspace.id, { include_archived: true });
            setArchivedSpaces((res.data || []).filter(s => s.is_archived));
        } catch (error) {
            console.error('Failed to load archived spaces:', error);
        }
    };

    useEffect(() => { loadArchivedSpaces(); }, [currentWorkspace]); // eslint-disable-line

    const handleRestoreSpace = async (spaceId) => {
        try {
            await spaceAPI.unarchive(spaceId);
            setArchivedSpaces(prev => prev.filter(s => s.id !== spaceId));
            const res = await spaceAPI.getByWorkspace(currentWorkspace.id);
            setSpaces(res.data || []);
            toast.success('Space dipulihkan');
        } catch (error) {
            toast.error('Gagal memulihkan space');
        }
    };

    const handleCreateSpace = async (data) => {
        try {
            const response = await spaceAPI.create({ ...data, workspace_id: currentWorkspace.id });
            setSpaces([...spaces, response.data]);
            toast.success('Space created');
            setIsModalOpen(false);
        } catch (error) {
            toast.error('Failed to create space');
        }
    };

    const handleUpdateSpace = async (spaceId, data) => {
        try {
            await spaceAPI.update(spaceId, data);
            const updated = spaces.map(s => s.id === spaceId ? { ...s, ...data } : s);
            setSpaces(updated);
            toast.success('Space updated');
            setEditingSpace(null);
        } catch (error) {
            toast.error('Failed to update space');
        }
    };

    const handleDeleteSpace = async (spaceId) => {
        if (!window.confirm('Delete this space and all its contents?')) return;
        try {
            await spaceAPI.delete(spaceId);
            setSpaces(spaces.filter(s => s.id !== spaceId));
            toast.success('Space deleted');
        } catch (error) {
            toast.error('Failed to delete space');
        }
    };

    // Load space channels
    useEffect(() => {
        if (currentWorkspace && spaces.length > 0) {
            loadSpaceChannels();
        }
    }, [currentWorkspace, spaces]);

    const loadSpaceChannels = async () => {
        try {
            const channelsMap = {};
            for (const space of spaces) {
                try {
                    const { data } = await api.get(`/workspaces/${currentWorkspace.id}/channels/spaces/${space.id}/channel`);
                    channelsMap[space.id] = data;
                } catch (error) {
                    // Channel doesn't exist yet, that's fine
                    channelsMap[space.id] = null;
                }
            }
            setSpaceChannels(channelsMap);
        } catch (error) {
            console.error('Failed to load space channels:', error);
        }
    };

    const handleCreateChannel = (space) => {
        setCreatingChannelFor(space);
    };

    const handleSaveChannel = async (channelData) => {
        if (!creatingChannelFor) return;
        
        // Update local state with new channel
        setSpaceChannels({
            ...spaceChannels,
            [creatingChannelFor.id]: channelData
        });
        
        setCreatingChannelFor(null);
    };

    const canManageChannel = currentUserRole === 'owner' || currentUserRole === 'admin';


    if (!currentWorkspace) {
        return (
            <Card className="border-brand-border shadow-card">
                <CardContent className="p-6">
                    <p className="text-brand-text-muted">Select a workspace first</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card className="border-brand-border shadow-card">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>Spaces</CardTitle>
                        <CardDescription>Organize your work into spaces</CardDescription>
                    </div>
                    <Button 
                        onClick={() => setIsModalOpen(true)}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        data-testid="create-space-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        New Space
                    </Button>
                </CardHeader>
                <CardContent>
                    {spaces.length > 0 ? (
                        <div className="space-y-3" x-dynamic="true">
                            {spaces.map(space => (
                                <div 
                                    key={space.id}
                                    className="p-4 border border-brand-border rounded-lg hover:bg-brand-accent-soft/30 transition-colors"
                                    data-testid={`space-item-${space.id}`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <div 
                                                className="w-4 h-4 rounded-full"
                                                style={{ backgroundColor: space.color }}
                                            />
                                            <div>
                                                <p className="font-medium text-brand-text-primary">{space.name}</p>
                                                {space.description && (
                                                    <p className="text-sm text-brand-text-muted">{space.description}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setEditingSpace(space)}
                                            >
                                                <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="text-brand-error hover:bg-brand-error/10"
                                                onClick={() => handleDeleteSpace(space.id)}
                                            >
                                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                            </Button>
                                        </div>
                                    </div>
                                    
                                    {/* Space Channel Info */}
                                    <div className="mt-3 pt-3 border-t border-brand-border">
                                        {spaceChannels[space.id] ? (
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Hash className="w-4 h-4 text-brand-primary" />
                                                    <span className="text-brand-text-primary font-medium">
                                                        {spaceChannels[space.id].name}
                                                    </span>
                                                    <span className="text-brand-text-muted text-xs">
                                                        (Space Channel)
                                                    </span>
                                                </div>
                                            </div>
                                        ) : canManageChannel ? (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleCreateChannel(space)}
                                                className="text-brand-primary border-brand-primary hover:bg-brand-primary/10"
                                            >
                                                <Plus className="w-3 h-3 mr-2" />
                                                Create Space Channel
                                            </Button>
                                        ) : (
                                            <p className="text-xs text-brand-text-muted">No channel yet</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-brand-text-muted py-8">No spaces yet. Create your first space!</p>
                    )}
                </CardContent>
            </Card>

            {/* Archived Spaces */}
            {archivedSpaces.length > 0 && (
                <Card className="border-brand-border shadow-card mt-6">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <ArchiveRestore className="w-5 h-5 text-amber-600" /> Space Terarsip
                        </CardTitle>
                        <CardDescription>Space yang disembunyikan dari navigasi. Pulihkan untuk menampilkannya kembali.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {archivedSpaces.map(space => (
                                <div
                                    key={space.id}
                                    className="p-4 border border-brand-border rounded-lg flex items-center justify-between"
                                    data-testid={`archived-space-item-${space.id}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: space.color }} />
                                        <p className="font-medium text-brand-text-primary">
                                            {space.prefix && <span className="text-brand-text-muted mr-1">{space.prefix}</span>}
                                            {space.name}
                                        </p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                        onClick={() => handleRestoreSpace(space.id)}
                                        data-testid={`restore-space-btn-${space.id}`}
                                    >
                                        <ArchiveRestore className="w-4 h-4 mr-2" /> Pulihkan
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Create/Edit Space Modal */}
            <SpaceModal
                isOpen={isModalOpen || !!editingSpace}
                onClose={() => { setIsModalOpen(false); setEditingSpace(null); }}
                space={editingSpace}
                onSave={editingSpace ? (data) => handleUpdateSpace(editingSpace.id, data) : handleCreateSpace}
            />
            
            {/* Create Space Channel Modal */}
            <CreateSpaceChannelModal
                isOpen={!!creatingChannelFor}
                onClose={() => setCreatingChannelFor(null)}
                space={creatingChannelFor}
                onSuccess={handleSaveChannel}
            />
        </>
    );
};

const SpaceModal = ({ isOpen, onClose, space, onSave }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [color, setColor] = useState('#2F6F63');
    const [prefix, setPrefix] = useState('');
    const [loading, setLoading] = useState(false);

    const colorOptions = ['#2F6F63', '#1F4D45', '#7ED957', '#F5C26B', '#F26B6B', '#6EC1E4', '#A7E3C8'];

    useEffect(() => {
        if (space) {
            setName(space.name);
            setDescription(space.description || '');
            setColor(space.color);
            setPrefix(space.prefix || '');
        } else {
            setName('');
            setDescription('');
            setColor('#2F6F63');
            setPrefix('');
        }
    }, [space, isOpen]);

    // Auto-generate prefix from name
    const handleNameChange = (value) => {
        setName(value);
        // Only auto-generate prefix for new spaces if prefix is empty
        if (!space && !prefix) {
            const autoPrefix = value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5);
            setPrefix(autoPrefix);
        }
    };

    const handlePrefixChange = (value) => {
        // Only allow uppercase letters, max 5 chars
        const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5);
        setPrefix(cleaned);
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            toast.error('Space name is required');
            return;
        }
        if (!prefix.trim()) {
            toast.error('Task prefix is required');
            return;
        }
        if (prefix.length < 2) {
            toast.error('Prefix must be at least 2 characters');
            return;
        }
        setLoading(true);
        await onSave({ name, description, color, prefix });
        setLoading(false);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent data-testid="space-modal">
                <DialogHeader>
                    <DialogTitle>{space ? 'Edit Space' : 'Create Space'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                    <div>
                        <Label>Name <span className="text-red-500">*</span></Label>
                        <Input
                            value={name}
                            onChange={(e) => handleNameChange(e.target.value)}
                            placeholder="Space name"
                            className="mt-1"
                            data-testid="space-name-input"
                        />
                    </div>
                    <div>
                        <Label>Task Prefix <span className="text-red-500">*</span></Label>
                        <div className="flex items-center gap-2 mt-1">
                            <Input
                                value={prefix}
                                onChange={(e) => handlePrefixChange(e.target.value)}
                                placeholder="e.g. TKL, GLRY"
                                className="w-32 uppercase font-mono"
                                maxLength={5}
                                data-testid="space-prefix-input"
                            />
                            <span className="text-sm text-brand-text-muted">
                                {prefix ? `${prefix}-123` : 'XXX-123'}
                            </span>
                        </div>
                        <p className="text-xs text-brand-text-muted mt-1">
                            2-5 characters. Used for task numbering (e.g., {prefix || 'TKL'}-1, {prefix || 'TKL'}-2)
                        </p>
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional description"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label>Color</Label>
                        <div className="flex items-center gap-3 mt-2">
                            <div className="relative">
                                <input
                                    type="color"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                                    title="Choose color"
                                />
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium">{color.toUpperCase()}</p>
                                <p className="text-xs text-gray-500">Click to choose custom color</p>
                            </div>
                        </div>
                        {/* Quick color presets */}
                        <div className="flex gap-2 mt-3">
                            {colorOptions.map(c => (
                                <button
                                    key={c}
                                    onClick={() => setColor(c)}
                                    className={cn(
                                        'w-6 h-6 rounded-full transition-all border border-gray-200',
                                        color === c && 'ring-2 ring-offset-1 ring-brand-primary'
                                    )}
                                    style={{ backgroundColor: c }}
                                    title={c}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button 
                        onClick={handleSubmit}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        disabled={loading}
                        data-testid="space-save-btn"
                    >
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {space ? 'Save' : 'Create'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};



// Lists/Sprints Settings
const ListsSettings = () => {
    const { currentWorkspace, spaces } = useWorkspace();
    const [lists, setLists] = useState([]);
    const [selectedSpace, setSelectedSpace] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingList, setEditingList] = useState(null);
    const [loading, setLoading] = useState(false);
    const groupOrder = ["Not Started", "Active", "Review", "Done"];

    useEffect(() => {
        if (selectedSpace) {
            fetchLists();
        }
    }, [selectedSpace]);

    useEffect(() => {
        if (spaces.length > 0 && !selectedSpace) {
            setSelectedSpace(spaces[0].id);
        }
    }, [spaces]);

    const fetchLists = async () => {
        setLoading(true);
        try {
            const response = await listAPI.getBySpace(selectedSpace);
            setLists(response.data);
        } catch (error) {
            console.error('Failed to fetch lists:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateList = async (data) => {
        try {
            const response = await listAPI.create({ ...data, space_id: selectedSpace });
            setLists([...lists, response.data]);
            toast.success('List created');
            setIsModalOpen(false);
        } catch (error) {
            toast.error('Failed to create list');
        }
    };

    const handleUpdateList = async (listId, data) => {
        try {
            await listAPI.update(listId, data);
            const updated = lists.map(l => l.id === listId ? { ...l, ...data } : l);
            setLists(updated);
            setIsModalOpen(false);
            toast.success('List updated');
            setEditingList(null);
        } catch (error) {
            toast.error('Failed to update list');
        }
    };

    const handleDeleteList = async (listId) => {
        if (!window.confirm('Delete this list and all its tasks?')) return;
        try {
            await listAPI.delete(listId);
            setLists(lists.filter(l => l.id !== listId));
            toast.success('List deleted');
        } catch (error) {
            toast.error('Failed to delete list');
        }
    };

    if (!currentWorkspace) {
        return (
            <Card className="border-brand-border shadow-card">
                <CardContent className="p-6">
                    <p className="text-brand-text-muted">Select a workspace first</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card className="border-brand-border shadow-card">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>Lists / Sprints</CardTitle>
                        <CardDescription>Create and manage task lists within spaces</CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                        <SearchableSelect
                            value={selectedSpace || ''}
                            onChange={setSelectedSpace}
                            options={spaces.map(space => ({
                                value: space.id,
                                label: space.name,
                                color: space.color,
                            }))}
                            placeholder="Select space"
                            className="w-48"
                        />
                        <Button 
                            onClick={() => { setEditingList(null); setIsModalOpen(true); }}
                            className="bg-brand-primary hover:bg-brand-primary-dark"
                            disabled={!selectedSpace}
                            data-testid="create-list-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            New List
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                        </div>
                    ) : lists.length > 0 ? (
                        <div className="space-y-3">
                            {lists.map(list => (
                                <div 
                                    key={list.id}
                                    className="flex items-center justify-between p-4 border border-brand-border rounded-lg hover:bg-brand-accent-soft/30 transition-colors"
                                    data-testid={`list-item-${list.id}`}
                                >
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <ListTodo className="w-4 h-4 text-brand-primary" strokeWidth={1.5} />
                                            <p className="font-medium text-brand-text-primary">{list.name}</p>
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            {list.statuses
                                                ?.sort((a, b) => {
                                                // urutkan berdasarkan group sesuai groupOrder
                                                const ga = groupOrder.indexOf(a.group);
                                                const gb = groupOrder.indexOf(b.group);
                                                if (ga !== gb) return ga - gb;

                                                // kalau group sama, urutkan berdasarkan order
                                                return a.order - b.order;
                                                })
                                                .map((status, idx) => (
                                                <span
                                                    key={idx}
                                                    className="text-xs px-2 py-0.5 rounded-full"
                                                    style={{
                                                    backgroundColor: `${status.color}20`,
                                                    color: status.color,
                                                    border: `1px solid ${status.color}40`
                                                    }}
                                                >
                                                    {status.name}
                                                </span>
                                                ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => { setEditingList(list); setIsModalOpen(true); }}
                                            data-testid={`edit-list-btn-${list.id}`}
                                        >
                                            <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="text-brand-error hover:bg-brand-error/10"
                                            onClick={() => handleDeleteList(list.id)}
                                            data-testid={`delete-list-btn-${list.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-brand-text-muted py-8">
                            {selectedSpace ? 'No lists yet. Create your first list!' : 'Select a space to manage lists'}
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit List Modal */}
            <ListModal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingList(null); }}
                list={editingList}
                onSave={editingList ? (data) => handleUpdateList(editingList.id, data) : handleCreateList}
            />
        </>
    );
};

const ListModal = ({ isOpen, onClose, list, onSave }) => {
    const [name, setName] = useState('');
    const [statuses, setStatuses] = useState([
        { name: 'To Do', group: 'Not Started', color: '#9FB0AC', order: 0 },
        { name: 'In Progress', group: 'Active', color: '#6EC1E4', order: 0 },
        { name: 'Review', group: 'Review', color: '#F5C26B', order: 0 },
        { name: 'Done', group: 'Done', color: '#7ED957', order: 0 },
    ]);
    const [newStatusName, setNewStatusName] = useState('');
    const [newStatusOrder, setNewStatusOrder] = useState(0);
    const [newStatusColor, setNewStatusColor] = useState('#2F6F63');
    const [editingStatus, setEditingStatus] = useState(null);
    const [loading, setLoading] = useState(false);

    
    const [activeId, setActiveId] = useState(null);

    const handleDragStart = ({ active }) => {
        setActiveId(active.id);
    };




    const [groups, setGroups] = useState([
        "Not Started",
        "Active",
        "Review",
        "Done",
    ]);

    const [openDetail, setOpenDetail] = useState(false);
    const [group, setGroup] = useState(null);
    const [status, setStatus] = useState(null);



    const colorOptions = ['#2F6F63', '#1F4D45', '#7ED957', '#F5C26B', '#F26B6B', '#6EC1E4', '#9FB0AC', '#A7E3C8'];

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (list) {
            setName(list.name);
            setStatuses(list.statuses.map(s => ({ ...s, group: s.group === null ? 'untagged' : s.group })) || []);
        } else {
            setName('');
            setStatuses([
                { name: 'To Do', group: 'Not Started', color: '#9FB0AC', order: 0 },
                { name: 'In Progress', group: 'Active', color: '#6EC1E4', order: 0 },
                { name: 'Review', group: 'Review', color: '#F5C26B', order: 0 },
                { name: 'Done', group: 'Done', color: '#7ED957', order: 0 },
            ]);
        }
    }, [list, isOpen]);


    // helper untuk generate order integer


    const generateOrder = (statuses, groupName) => {
        const itemsInGroup = statuses
            .filter(s => s.group === groupName)
            .map(s => s.order);

        if (itemsInGroup.length === 0) return 0;

        // cari angka terkecil yang belum dipakai
        let order = 0;
        while (itemsInGroup.includes(order)) {
            order++;
        }
        return order;
    };


    const handleDragEnd = ({ active, over }) => {
        setActiveId(null);
        const activeId = active?.id;
        const overId = over?.id;

        if(!activeId || !overId) return

        setStatuses((prev) => {
            const activeStatus = prev.find(s => s.name === activeId);

            // --- 1. Drop ke placeholder group kosong ---
            const targetGroup = groups.find(g => overId === `drop-${g}`);
            if (targetGroup) {
            return prev.map(s =>
                s.name === activeId
                ? { ...s, group: targetGroup, order: generateOrder(prev, targetGroup) }
                : s
            );
            }

            // --- 2. Drop ke group container langsung ---
            if (groups.includes(overId)) {
            return prev.map(s =>
                s.name === activeId
                ? { ...s, group: overId, order: generateOrder(prev, overId) }
                : s
            );
            }

            // --- 3. Drop ke item lain ---
            const overStatus = prev.find(s => s.name === overId);
            if (activeStatus && overStatus) {
            // pindah antar group
            if (activeStatus.group !== overStatus.group) {
                return prev.map(s =>
                s.name === activeId
                    ? { ...s, group: overStatus.group, order: generateOrder(prev, overStatus.group) }
                    : s
                );
            }

            // reorder dalam group yang sama
            const sameGroupItems = prev.filter(s => s.group === activeStatus.group);
            const oldIndex = sameGroupItems.findIndex(s => s.name === activeId);
            const newIndex = sameGroupItems.findIndex(s => s.name === overId);

            const reordered = arrayMove(sameGroupItems, oldIndex, newIndex).map((s, idx) => ({
                ...s,
                order: idx, // update order sesuai posisi baru
            }));

            return [
                ...prev.filter(s => s.group !== activeStatus.group),
                ...reordered
            ];
            }

            return prev;
        });
    };

    const handleAddStatus = () => {
        if (newStatusName.trim()) {
            // Check if status already exists
            if (statuses.some(s => s.name.toLowerCase() === newStatusName.trim().toLowerCase())) {
            toast.error('Status already exists');
            return;
            }

            setStatuses([
            ...statuses,
            {
                name: newStatusName.trim(),
                color: newStatusColor,
                group: group,
                order: generateOrder(statuses, group) // gunakan generateOrder di sini
            }
            ]);

            setNewStatusOrder(0);
            setNewStatusName('');
            setNewStatusColor('#2F6F63');
            setOpenDetail(false);
        } else {
            toast.error('Status name is required');
        }
    };


    const handleRemoveStatus = (statusName) => {
        if (statuses.length <= 1) {
            toast.error('At least one status is required');
            return;
        }
        const newStatuses = statuses.filter(s => s.name !== statusName)
            .map((s, i) => ({ ...s, order: i }));
        setStatuses(newStatuses);
    };

    const handleEditStatus = (status) => {
        setGroup(status.group);
        setEditingStatus(status);
        setNewStatusName(status.name);
        setNewStatusOrder(status.order);
        setNewStatusColor(status.color);
        setOpenDetail(true);
    };

    const handleUpdateStatus = () => {
        if (!newStatusName.trim()) return;
        setStatuses(statuses.map(s => 
            s.name === editingStatus.name 
                ? { ...s, name: newStatusName.trim(), color: newStatusColor }
                : s
        ));
        setEditingStatus(null);
        setNewStatusOrder(0);
        setNewStatusName('');
        setNewStatusColor('#2F6F63');
        setOpenDetail(false);
    };

    const handleAddStatusGroup = (group) => {
        setGroup(group);
        setNewStatusOrder(() => generateOrder(statuses, group));
        setOpenDetail(true);
    }

    const handleSubmit = async () => {
        if (!name.trim()) {
            toast.error('List name is required');
            return;
        }
        if (statuses.length === 0) {
            toast.error('At least one status is required');
            return;
        }
        setLoading(true);
        await onSave({ name, statuses });
        setLoading(false);
    };



    return (
        <>
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-3xl" data-testid="list-modal">
                <DialogHeader>
                    <DialogTitle>{list ? 'Edit List' : 'Create List'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                    <div>
                        <Label>List Name</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Sprint 1, Backlog, Q1 Tasks"
                            className="mt-1"
                            data-testid="list-name-input"
                        />
                    </div>
                    {/* Statuses with Drag & Drop */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label>Workflow</Label>
                        </div>
                        <p className="text-xs text-brand-text-muted mb-6">Define the workflow stages for tasks in this list</p>
                        <div className="grid grid-cols-2 gap-2 select-none">
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                                // onDragCancel={() => setActiveId(null)}
                                modifiers={[snapCenterToCursor, restrictToWindowEdges]}
                                >

                                {/* Overlay untuk item yang sedang di-drag */}
                                <DragOverlay>
                                    {activeId ? (
                                        <SortableStatusItem
                                            status={statuses.find(s => s.name === activeId)}
                                            index={0}
                                            onEdit={handleEditStatus}
                                            onDelete={handleRemoveStatus}
                                            // styling khusus saat drag
                                            // className="w-48 h-16 shadow-lg rounded-lg bg-white"
                                        />
                                    ) : null}
                                </DragOverlay>
                            
                            {
                                
                                groups.map((group) => <div className="col-span-1 flex flex-col gap-2"  id={group}>
                                    <div className="flex pb-2 border-b border-b-solid border-b-brand items-start gap-1">
                                        <ArrowUpFromDotIcon className="w-4 h-4 text-brand-primary-dark rotate-90" />
                                        <div className="flex flex-col gap-1">
                                            <Label className="font-semibold text-brand-primary-dark">{group}</Label>
                                            <span className="text-xs text-brand-text-muted">Drag to reorder</span>
                                        </div>
                                        <Button onClick={() => handleAddStatusGroup(group)} className="ml-auto" size="sm" variant="ghost" title="Add New Status">
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                        <SortableContext
                                            key={group}
                                            id={group} // id container group
                                            items={statuses.filter(s => s.group === group).map(s => s.name)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            <GroupContainer group={group}>
                                                {statuses.filter(s => s.group === group).map((status) => (
                                                    <SortableStatusItem
                                                    className={`mb-2 ${activeId != null && `cursor-grabbing`}`}
                                                    key={status.name}
                                                    id={status.name}
                                                    status={status}
                                                    index={status.order}
                                                    onEdit={handleEditStatus}
                                                    onDelete={handleRemoveStatus}
                                                    />
                                                ))}
                                            </GroupContainer>
                                        </SortableContext>
                                </div>)

                            }

                            { statuses.filter(s => s.group === 'untagged').length > 0 && <div className="col-span-1 flex flex-col gap-2"  id="untagged">
                                <div className="flex pb-2 border-b border-b-solid border-b-brand items-start gap-1">
                                    {/* <ArrowUpFromDotIcon className="w-4 h-4 text-brand-primary-dark rotate-90" /> */}
                                    <div className="flex flex-col gap-1">
                                        <Label className="font-semibold text-brand-primary-dark">Untagged</Label>
                                        <span className="text-xs text-brand-text-muted">*Note: these status will not be saved in database anymore</span>
                                    </div>
                                </div>
                                <SortableContext
                                    key={"untagged"}
                                    id={"untagged"} // id container group
                                    items={statuses.filter(s => s.group === 'untagged').map(s => s.name)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <GroupContainer group={group} enableDrop={false}>
                                        {statuses.filter(s => s.group === 'untagged').map((status) => (
                                            <SortableStatusItem
                                            className={`mb-2 ${activeId != null && `cursor-grabbing`}`}
                                            key={status.name}
                                            id={status.group}
                                            status={status}
                                            index={status.order}
                                            onEdit={handleEditStatus}
                                            onDelete={handleRemoveStatus}
                                            />
                                        ))}
                                    </GroupContainer>
                                </SortableContext>
                            </div>}
                            
                        </DndContext>
                        </div>

                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <div className="flex gap-3">
                        <Button variant="outline" onClick={onClose}>Cancel</Button>
                        <Button 
                            onClick={handleSubmit}
                            className="bg-brand-primary hover:bg-brand-primary-dark"
                            disabled={loading}
                            data-testid="list-save-btn"
                        >
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {list ? 'Save' : 'Create'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
        <Dialog open={openDetail} onOpenChange={setOpenDetail} >
            <DialogContent className="max-w-lg" data-testid="list-modal" overlayClassName="!bg-black/30">
                <DialogHeader>
                    <DialogTitle>{editingStatus ? 'Edit Status' : 'Add Status'}
                        <span className="text-brand-text-muted"> - ({group})</span>
                    </DialogTitle>
                </DialogHeader>
                <div className="">
                    {/* <p className="text-xs font-medium mb-2">
                        {editingStatus ? 'Edit Status' : 'Add New Status'}
                    </p> */}
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-4">
                            <div className="flex-1 flex flex-col gap-2">
                                <Label>Name</Label>
                                <Input
                                    value={newStatusName}
                                    onChange={(e) => setNewStatusName(e.target.value)}
                                    placeholder="Status name"
                                />
                            </div>
                            <div className="flex-1 flex flex-col gap-2 max-w-20">
                                <Label>Order</Label>
                                <Input
                                    min={0}
                                    type="number"
                                    value={newStatusOrder}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value.replace(/[^0-9]/g, ''));
                                        setNewStatusOrder(val);
                                    }}
                                    placeholder="Status name"
                                />
                            </div>
                        </div>
                        <div className="flex-1 flex flex-col gap-2">
                            <Label>Color</Label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    value={newStatusColor}
                                    onChange={(e) => setNewStatusColor(e.target.value)}
                                    className="w-10 h-10 rounded cursor-pointer border border-gray-200"
                                    title="Choose color"
                                />
                                <span className="text-sm text-gray-600">{newStatusColor.toUpperCase()}</span>
                            </div>
                            {/* Quick presets */}
                            <div className="flex gap-1 mt-1">
                                {colorOptions.slice(0, 5).map(c => (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => setNewStatusColor(c)}
                                        className={cn(
                                            'w-5 h-5 rounded-full transition-all border border-gray-200',
                                            newStatusColor === c && 'ring-2 ring-offset-1 ring-brand-primary'
                                        )}
                                        style={{ backgroundColor: c }}
                                        title={c}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="flex-1 flex gap-2 justify-end">
                            {editingStatus ? (
                                <>
                                    <Button variant="outline" onClick={() => {
                                        setNewStatusOrder(0);
                                        setEditingStatus(null);
                                        setNewStatusName('');
                                        setNewStatusColor('#2F6F63');
                                        setOpenDetail(false);
                                    }}>Cancel</Button>
                                    <Button onClick={handleUpdateStatus} disabled={!newStatusName.trim() || newStatusOrder < 0} >Save</Button>
                                </>
                            ) : (
                                <Button variant="outline" onClick={handleAddStatus} disabled={!newStatusName.trim() || newStatusOrder < 0}>
                                    Add
                                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
        </>
    );
};


// Custom Fields Settings
const CustomFieldsSettings = () => {
    const { currentSpace, spaces } = useWorkspace();
    const [fields, setFields] = useState([]);
    const [selectedSpace, setSelectedSpace] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (selectedSpace) {
            fetchFields();
        }
    }, [selectedSpace]);

    useEffect(() => {
        if (spaces.length > 0 && !selectedSpace) {
            setSelectedSpace(spaces[0].id);
        }
    }, [spaces]);

    const fetchFields = async () => {
        setLoading(true);
        try {
            const response = await customFieldAPI.getBySpace(selectedSpace);
            setFields(response.data);
        } catch (error) {
            console.error('Failed to fetch custom fields:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateField = async (data) => {
        try {
            await customFieldAPI.create({ ...data, space_id: selectedSpace });
            toast.success('Custom field created');
            fetchFields();
            setIsModalOpen(false);
        } catch (error) {
            toast.error('Failed to create field');
        }
    };

    const handleDeleteField = async (fieldId) => {
        if (!window.confirm('Delete this custom field?')) return;
        try {
            await customFieldAPI.delete(fieldId);
            setFields(fields.filter(f => f.id !== fieldId));
            toast.success('Field deleted');
        } catch (error) {
            toast.error('Failed to delete field');
        }
    };

    const handleToggleField = async (field) => {
        try {
            await customFieldAPI.update(field.id, { is_enabled: !field.is_enabled });
            setFields(fields.map(f => f.id === field.id ? { ...f, is_enabled: !f.is_enabled } : f));
        } catch (error) {
            toast.error('Failed to update field');
        }
    };

    const getFieldIcon = (type) => {
        switch (type) {
            case 'text': return <Type className="w-4 h-4" strokeWidth={1.5} />;
            case 'number': return <Hash className="w-4 h-4" strokeWidth={1.5} />;
            case 'date': return <Calendar className="w-4 h-4" strokeWidth={1.5} />;
            case 'dropdown': return <ChevronDown className="w-4 h-4" strokeWidth={1.5} />;
            case 'multi_select': return <List className="w-4 h-4" strokeWidth={1.5} />;
            case 'checkbox': return <CheckSquare className="w-4 h-4" strokeWidth={1.5} />;
            case 'url': return <Link className="w-4 h-4" strokeWidth={1.5} />;
            case 'currency': return <DollarSign className="w-4 h-4" strokeWidth={1.5} />;
            default: return <Type className="w-4 h-4" strokeWidth={1.5} />;
        }
    };

    return (
        <>
            <Card className="border-brand-border shadow-card">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>Custom Fields</CardTitle>
                        <CardDescription>Add custom fields to track any data</CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                        <SearchableSelect
                            value={selectedSpace || ''}
                            onChange={setSelectedSpace}
                            options={spaces.map(space => ({
                                value: space.id,
                                label: space.name,
                                color: space.color,
                            }))}
                            placeholder="Select space"
                            className="w-48"
                        />
                        <Button 
                            onClick={() => setIsModalOpen(true)}
                            className="bg-brand-primary hover:bg-brand-primary-dark"
                            disabled={!selectedSpace}
                            data-testid="create-field-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            New Field
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                        </div>
                    ) : fields.length > 0 ? (
                        <div className="space-y-3">
                            {fields.map(field => (
                                <div 
                                    key={field.id}
                                    className={cn(
                                        'flex items-center justify-between p-4 border rounded-lg transition-colors',
                                        field.is_enabled ? 'border-brand-border' : 'border-brand-border/50 opacity-60'
                                    )}
                                    data-testid={`field-item-${field.id}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-brand-accent-soft rounded-lg flex items-center justify-center">
                                            {getFieldIcon(field.field_type)}
                                        </div>
                                        <div>
                                            <p className="font-medium text-brand-text-primary">{field.name}</p>
                                            <p className="text-sm text-brand-text-muted capitalize">
                                                {field.field_type.replace('_', ' ')}
                                                {field.is_required && ' • Required'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Switch
                                            checked={field.is_enabled}
                                            onCheckedChange={() => handleToggleField(field)}
                                        />
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="text-brand-error hover:bg-brand-error/10"
                                            onClick={() => handleDeleteField(field.id)}
                                        >
                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-brand-text-muted py-8">
                            {selectedSpace ? 'No custom fields yet' : 'Select a space to manage fields'}
                        </p>
                    )}
                </CardContent>
            </Card>

            <CustomFieldModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleCreateField}
            />
        </>
    );
};

const CustomFieldModal = ({ isOpen, onClose, onSave }) => {
    const [name, setName] = useState('');
    const [fieldType, setFieldType] = useState('text');
    const [options, setOptions] = useState('');
    const [isRequired, setIsRequired] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        if (!name.trim()) {
            toast.error('Field name is required');
            return;
        }
        setLoading(true);
        const data = {
            name,
            field_type: fieldType,
            is_required: isRequired,
        };
        if (fieldType === 'dropdown' || fieldType === 'multi_select') {
            data.options = options.split(',').map(o => o.trim()).filter(Boolean);
        }
        await onSave(data);
        setLoading(false);
        setName('');
        setFieldType('text');
        setOptions('');
        setIsRequired(false);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent data-testid="custom-field-modal">
                <DialogHeader>
                    <DialogTitle>Create Custom Field</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                    <div>
                        <Label>Field Name</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., Sprint, Story Points"
                            className="mt-1"
                            data-testid="field-name-input"
                        />
                    </div>
                    <div>
                        <Label>Field Type</Label>
                        <Select value={fieldType} onValueChange={setFieldType}>
                            <SelectTrigger className="mt-1" data-testid="field-type-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="text">Text</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                                <SelectItem value="date">Date</SelectItem>
                                <SelectItem value="dropdown">Dropdown</SelectItem>
                                <SelectItem value="multi_select">Multi-select</SelectItem>
                                <SelectItem value="checkbox">Checkbox</SelectItem>
                                <SelectItem value="url">URL</SelectItem>
                                <SelectItem value="currency">Currency</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {(fieldType === 'dropdown' || fieldType === 'multi_select') && (
                        <div>
                            <Label>Options (comma-separated)</Label>
                            <Input
                                value={options}
                                onChange={(e) => setOptions(e.target.value)}
                                placeholder="Option 1, Option 2, Option 3"
                                className="mt-1"
                            />
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <Switch checked={isRequired} onCheckedChange={setIsRequired} />
                        <Label>Required field</Label>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button 
                        onClick={handleSubmit}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        disabled={loading}
                        data-testid="field-save-btn"
                    >
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Create
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Webhooks Settings
const WebhooksSettings = () => {
    const { currentWorkspace } = useWorkspace();
    const [webhooks, setWebhooks] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (currentWorkspace) {
            fetchWebhooks();
        }
    }, [currentWorkspace]);

    const fetchWebhooks = async () => {
        setLoading(true);
        try {
            const response = await webhookAPI.getByWorkspace(currentWorkspace.id);
            setWebhooks(response.data);
        } catch (error) {
            console.error('Failed to fetch webhooks:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateWebhook = async (data) => {
        try {
            await webhookAPI.create({ ...data, workspace_id: currentWorkspace.id });
            toast.success('Webhook created');
            fetchWebhooks();
            setIsModalOpen(false);
        } catch (error) {
            toast.error('Failed to create webhook');
        }
    };

    const handleDeleteWebhook = async (webhookId) => {
        if (!window.confirm('Delete this webhook?')) return;
        try {
            await webhookAPI.delete(webhookId);
            setWebhooks(webhooks.filter(w => w.id !== webhookId));
            toast.success('Webhook deleted');
        } catch (error) {
            toast.error('Failed to delete webhook');
        }
    };

    const handleToggleWebhook = async (webhook) => {
        try {
            await webhookAPI.update(webhook.id, { is_active: !webhook.is_active });
            setWebhooks(webhooks.map(w => w.id === webhook.id ? { ...w, is_active: !w.is_active } : w));
        } catch (error) {
            toast.error('Failed to update webhook');
        }
    };

    if (!currentWorkspace) {
        return (
            <Card className="border-brand-border shadow-card">
                <CardContent className="p-6">
                    <p className="text-brand-text-muted">Select a workspace first</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card className="border-brand-border shadow-card">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>Webhooks</CardTitle>
                        <CardDescription>Integrate with external services</CardDescription>
                    </div>
                    <Button 
                        onClick={() => setIsModalOpen(true)}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        data-testid="create-webhook-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        New Webhook
                    </Button>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                        </div>
                    ) : webhooks.length > 0 ? (
                        <div className="space-y-3">
                            {webhooks.map(webhook => (
                                <div 
                                    key={webhook.id}
                                    className={cn(
                                        'flex items-center justify-between p-4 border rounded-lg',
                                        webhook.is_active ? 'border-brand-border' : 'border-brand-border/50 opacity-60'
                                    )}
                                >
                                    <div>
                                        <p className="font-medium text-brand-text-primary font-mono text-sm truncate max-w-md">
                                            {webhook.url}
                                        </p>
                                        <div className="flex gap-2 mt-1">
                                            {webhook.events.map(event => (
                                                <span key={event} className="text-xs px-2 py-0.5 bg-brand-accent-soft rounded-full">
                                                    {event}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Switch
                                            checked={webhook.is_active}
                                            onCheckedChange={() => handleToggleWebhook(webhook)}
                                        />
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="text-brand-error hover:bg-brand-error/10"
                                            onClick={() => handleDeleteWebhook(webhook.id)}
                                        >
                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-brand-text-muted py-8">No webhooks configured</p>
                    )}
                </CardContent>
            </Card>

            <WebhookModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleCreateWebhook}
            />
        </>
    );
};

const WebhookModal = ({ isOpen, onClose, onSave }) => {
    const [url, setUrl] = useState('');
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);

    const eventOptions = ['task.created', 'task.updated', 'task.completed', 'task.deleted'];

    const toggleEvent = (event) => {
        if (events.includes(event)) {
            setEvents(events.filter(e => e !== event));
        } else {
            setEvents([...events, event]);
        }
    };

    const handleSubmit = async () => {
        if (!url.trim()) {
            toast.error('Webhook URL is required');
            return;
        }
        if (events.length === 0) {
            toast.error('Select at least one event');
            return;
        }
        setLoading(true);
        await onSave({ url, events });
        setLoading(false);
        setUrl('');
        setEvents([]);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create Webhook</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                    <div>
                        <Label>Webhook URL</Label>
                        <Input
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://your-server.com/webhook"
                            className="mt-1"
                            data-testid="webhook-url-input"
                        />
                    </div>
                    <div>
                        <Label>Events</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {eventOptions.map(event => (
                                <button
                                    key={event}
                                    onClick={() => toggleEvent(event)}
                                    className={cn(
                                        'px-3 py-1.5 rounded-full text-sm transition-colors',
                                        events.includes(event) 
                                            ? 'bg-brand-primary text-white' 
                                            : 'bg-brand-accent-soft text-brand-text-secondary hover:bg-brand-accent-soft/80'
                                    )}
                                >
                                    {event}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button 
                        onClick={handleSubmit}
                        className="bg-brand-primary hover:bg-brand-primary-dark"
                        disabled={loading}
                        data-testid="webhook-save-btn"
                    >
                        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Create
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Audit Log Settings
const AuditLogSettings = () => {
    const { currentWorkspace } = useWorkspace();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [entityFilter, setEntityFilter] = useState('all');

    useEffect(() => {
        if (currentWorkspace) {
            fetchLogs();
        }
    }, [currentWorkspace, entityFilter]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = entityFilter !== 'all' ? { entity_type: entityFilter } : {};
            const response = await auditLogAPI.getByWorkspace(currentWorkspace.id, params);
            setLogs(response.data);
        } catch (error) {
            console.error('Failed to fetch audit logs:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!currentWorkspace) {
        return (
            <Card className="border-brand-border shadow-card">
                <CardContent className="p-6">
                    <p className="text-brand-text-muted">Select a workspace first</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="border-brand-border shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle>Audit Log</CardTitle>
                    <CardDescription>Track all changes in your workspace</CardDescription>
                </div>
                <Select value={entityFilter} onValueChange={setEntityFilter}>
                    <SelectTrigger className="w-32">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="task">Tasks</SelectItem>
                        <SelectItem value="space">Spaces</SelectItem>
                        <SelectItem value="list">Lists</SelectItem>
                    </SelectContent>
                </Select>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
                    </div>
                ) : logs.length > 0 ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {logs.map(log => (
                            <div 
                                key={log.id}
                                className="flex items-start gap-3 p-3 border-b border-brand-border last:border-0"
                            >
                                <div className="w-8 h-8 bg-brand-accent-soft rounded-full flex items-center justify-center flex-shrink-0">
                                    <History className="w-4 h-4 text-brand-primary" strokeWidth={1.5} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm">
                                        <span className="font-medium">{log.user_name}</span>
                                        {' '}{log.action}{' '}
                                        <span className="text-brand-text-muted">{log.entity_type}</span>
                                    </p>
                                    <p className="text-xs text-brand-text-muted mt-0.5">
                                        {formatDateTime(log.created_at)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-center text-brand-text-muted py-8">No activity recorded yet</p>
                )}
            </CardContent>
        </Card>
    );
};
